import { useAppStateStore } from '@client/stores/AppStateStore';
import { isTrpcExecutionDeadlineError } from '@client/trpc/sessionHandling';
import { textToHtml } from '@client/utils/common/text-to-html';
import {
    categoriseNetworkError,
    getHttpStatus,
    isNetworkError,
    isServerError,
    isServerErrorByMessage,
    type NetworkErrorDetails,
} from '@client/utils/errors/network-error-detection';
import { debug } from '@client/utils/logger';
import { EXECUTION_DEADLINE_MESSAGE } from '@shared/constants/system/error-code';
import { AlertCircle, AlertTriangle, Bot, Clock, ExternalLink, Globe, Server, WifiOff } from 'lucide-react';
import type * as React from 'react';
import { createFieldNameFromPath, humanizeFieldName } from './fieldNameUtils';
import type { ZodIssue } from './types';

// ============================================================================
// Server Error Handling (5xx HTTP errors)
// ============================================================================

interface ServerErrorDetails {
    type: 'internal_error' | 'bad_gateway' | 'service_unavailable' | 'gateway_timeout' | 'unknown_server_error';
    statusCode?: number;
    title: string;
    userMessage: string;
    suggestions: string[];
    technicalMessage?: string;
}

/**
 * Check if an error message is generic/unhelpful
 */
function isGenericErrorMessage(message: string | undefined): boolean {
    if (!message) return true;
    const genericMessages = [
        'unknown error',
        'an error occurred',
        'internal server error',
        'something went wrong',
        'error',
    ];
    return genericMessages.some((generic) => message.toLowerCase().includes(generic));
}

/**
 * Categorise a server error by HTTP status code
 */
function categoriseServerError(error: unknown): ServerErrorDetails {
    const httpStatus = getHttpStatus(error);
    const originalMessage = error instanceof Error ? error.message : String(error);
    const hasMeaningfulMessage = !isGenericErrorMessage(originalMessage);

    // If we detected this as a server error via message but have no httpStatus,
    // default to 500 (most common server error). This handles cases where:
    // - Server is down
    // - Error occurs before tRPC processing
    // - Proxy returns 500 without tRPC error data
    const inferredStatus = httpStatus ?? (isServerErrorByMessage(error) ? 500 : undefined);

    const baseDetails = {
        statusCode: inferredStatus,
        technicalMessage: hasMeaningfulMessage
            ? originalMessage
            : inferredStatus
              ? `HTTP ${inferredStatus}`
              : undefined,
    };

    switch (inferredStatus) {
        case 500:
            return {
                ...baseDetails,
                type: 'internal_error',
                title: 'Server Error',
                userMessage: 'Something went wrong on our end. Our team has been notified.',
                suggestions: [
                    'Try again in a few moments',
                    'If the problem persists, contact support',
                    'Your data has not been affected',
                ],
            };
        case 502:
            return {
                ...baseDetails,
                type: 'bad_gateway',
                title: 'Service Temporarily Unavailable',
                userMessage: "We're having trouble connecting to our servers.",
                suggestions: ['Wait a few seconds and try again', 'Check back in a few minutes if the issue continues'],
            };
        case 503:
            return {
                ...baseDetails,
                type: 'service_unavailable',
                title: 'Service Unavailable',
                userMessage: 'The service is temporarily unavailable, possibly due to maintenance.',
                suggestions: ['Try again in a few minutes', 'Check our status page for any ongoing maintenance'],
            };
        case 504:
            return {
                ...baseDetails,
                type: 'gateway_timeout',
                title: 'Request Timeout',
                userMessage: 'The server took too long to respond.',
                suggestions: [
                    'Try again - this is usually temporary',
                    'If you were submitting data, check if it was saved before retrying',
                ],
            };
        // 524 is Cloudflare's origin-timeout (backend did not respond within the edge
        // timeout, so an HTML error page is returned that fails JSON parsing). Surface it
        // to users as a transient outage rather than the raw JSON-parse error.
        case 524:
            return {
                ...baseDetails,
                type: 'service_unavailable',
                title: 'Service temporarily unavailable',
                userMessage: 'The request took too long to complete. Please try again in a few moments.',
                suggestions: [
                    'Wait a few moments and try again',
                    'If you were submitting data, check whether it was saved before retrying',
                ],
            };
        default:
            return {
                ...baseDetails,
                type: 'unknown_server_error',
                title: 'Server Error',
                userMessage: 'An unexpected server error occurred.',
                suggestions: ['Try again in a few moments', 'Contact support if the problem persists'],
            };
    }
}

/**
 * Format a server error for display
 */
function formatServerError(errorDetails: ServerErrorDetails): React.ReactNode {
    return (
        <div className="space-y-4">
            {/* Header with icon and message */}
            <div className="flex items-start gap-3 border-separator border-b pb-3">
                <Server className="text-red-500 dark:text-red-400" size={20} />
                <div className="flex-1">
                    <p className="mb-1 font-medium text-foreground">{errorDetails.title}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{errorDetails.userMessage}</p>
                </div>
            </div>

            {/* Suggestions */}
            {errorDetails.suggestions.length > 0 && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/30">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" size={16} />
                        <div className="flex-1">
                            <p className="mb-2 font-medium text-blue-900 text-sm dark:text-blue-100">
                                What you can do:
                            </p>
                            <ul className="space-y-1 text-blue-800 text-sm dark:text-blue-200">
                                {errorDetails.suggestions.map((suggestion, index) => (
                                    <li key={index} className="flex items-start gap-2">
                                        <span className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400">•</span>
                                        <span>{suggestion}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Technical details - only show if meaningful */}
            {errorDetails.technicalMessage && (
                <div className="rounded-lg border border-separator bg-muted p-3">
                    <p className="mb-1 font-medium text-subtle-foreground text-xs">Technical Details</p>
                    <p className="break-all font-mono text-muted-foreground text-xs">{errorDetails.technicalMessage}</p>
                </div>
            )}
        </div>
    );
}

export function formatErrorMessage(error: Error | string | unknown | unknown[]): React.ReactNode {
    // Debug logging to see the actual error structure
    debug('[ErrorFormatters] Error object:', error);
    debug('[ErrorFormatters] Error.data:', (error as any)?.data);
    debug('[ErrorFormatters] Error.data.code:', (error as any)?.data?.code);
    debug('[ErrorFormatters] Error.data.httpStatus:', (error as any)?.data?.httpStatus);

    // Handle array of errors
    if (Array.isArray(error)) {
        debug('[ErrorFormatters] Error is an array:', error);

        if (error.length === 0) {
            return (
                <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 flex-shrink-0 text-warning" size={18} />
                    <div>
                        <p className="font-medium text-foreground">No Errors</p>
                        <p className="mt-0.5 text-muted-foreground text-sm">An empty error array was provided.</p>
                    </div>
                </div>
            );
        }

        if (error.length === 1) {
            // If there's only one error, format it as a single error
            return formatErrorMessage(error[0]);
        }

        // Handle multiple errors
        return (
            <div className="space-y-4">
                <div className="flex items-start gap-3 border-separator border-b pb-3">
                    <div>
                        <p className="text-muted-foreground text-sm">
                            Please review and resolve the following {error.length} issues:
                        </p>
                    </div>
                </div>
                <div className="space-y-4">
                    {error.map((singleError, index) => (
                        <div key={index} className="border-red-200 border-l-2 pl-3 dark:border-red-700">
                            <div className="mb-1 font-medium text-subtle-foreground text-xs">Issue {index + 1}</div>
                            {formatErrorMessage(singleError)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Preserve the unknown-write-outcome advice instead of generic retry copy.
    if (isTrpcExecutionDeadlineError(error)) {
        return <p className="text-foreground">{EXECUTION_DEADLINE_MESSAGE}</p>;
    }

    // Handle network errors
    if (isNetworkError(error)) {
        // Get connection state from store
        const connectionState = useAppStateStore.getState().connectionState;
        const networkErrorDetails = categoriseNetworkError(
            error,
            connectionState.quality,
            connectionState.consecutiveFailures,
        );
        return formatNetworkError(networkErrorDetails);
    }

    // Handle server errors (5xx HTTP status)
    if (isServerError(error)) {
        debug('[ErrorFormatters] Detected server error:', error);
        const serverErrorDetails = categoriseServerError(error);
        return formatServerError(serverErrorDetails);
    }

    // Handle AI provider errors
    if (error instanceof Error || typeof error === 'string') {
        const errorMessage = error instanceof Error ? error.message : error;
        const aiProviderError = parseAiProviderError(errorMessage);
        if (aiProviderError) {
            return formatAiProviderError(aiProviderError);
        }
    }

    // Handle TRPCClientError with JSON message containing Zod issues
    if (error instanceof Error) {
        debug('[ErrorFormatters] Error message:', error.message);

        // Try to parse the error message as JSON (for TRPCClientError)
        try {
            const parsedMessage = JSON.parse(error.message);
            if (Array.isArray(parsedMessage)) {
                debug('[ErrorFormatters] Found JSON array in error message:', parsedMessage);

                // Check if it looks like Zod issues
                const isZodIssues = parsedMessage.every(
                    (item) =>
                        item &&
                        typeof item === 'object' &&
                        'path' in item &&
                        'message' in item &&
                        Array.isArray(item.path),
                );

                if (isZodIssues) {
                    debug('[ErrorFormatters] Parsed Zod issues from error message');
                    const issues = parsedMessage as ZodIssue[];
                    return formatZodIssues(issues);
                }
            }
        } catch {
            debug('[ErrorFormatters] Error message is not valid JSON, continuing with other checks');
        }
    }

    if (error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object') {
        debug('[ErrorFormatters] Error data:', error.data);

        // Check for zodError
        if ('zodError' in error.data && error.data.zodError && typeof error.data.zodError === 'object') {
            const zodError = error.data.zodError;
            debug('[ErrorFormatters] Zod error:', zodError);

            // Handle the issues format (newer Zod format)
            if ('issues' in zodError && Array.isArray(zodError.issues)) {
                debug('[ErrorFormatters] Found issues format');
                const issues = zodError.issues as ZodIssue[];
                return formatZodIssues(issues);
            }

            // Handle the fieldErrors format (older format)
            if ('fieldErrors' in zodError && zodError.fieldErrors && typeof zodError.fieldErrors === 'object') {
                debug('[ErrorFormatters] Found fieldErrors format');
                const fieldErrors = zodError.fieldErrors as Record<string, string[]>;
                return formatFieldErrors(fieldErrors);
            }
        }

        // Check if the error itself contains issues directly
        if ('issues' in error.data && Array.isArray(error.data.issues)) {
            debug('[ErrorFormatters] Found issues directly in error.data');
            const issues = error.data.issues as ZodIssue[];
            return formatZodIssues(issues);
        }
    }

    // Fallback for other error types
    const errorHtmlString =
        error instanceof Error
            ? textToHtml(error.message)
            : typeof error === 'string'
              ? textToHtml(error)
              : error && typeof error === 'object' && 'toString' in error
                ? textToHtml(error.toString())
                : 'An unknown error occurred.';

    return (
        <div className="flex items-start gap-3">
            <div
                className="flex-1 text-muted-foreground text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: errorHtmlString }}
            />
        </div>
    );
}

interface AiProviderError {
    provider: string;
    attempts: number;
    errorDetails: {
        code: number;
        message: string;
        status: string;
    };
    learnMoreUrl?: string;
}

function parseAiProviderError(errorMessage: string): AiProviderError | null {
    // Pattern to match AI provider errors
    const aiProviderPattern = /^AI provider error after (\d+) attempts?: (.+?) Adapter Error: (.+)$/;
    const match = errorMessage.match(aiProviderPattern);

    if (!match) {
        return null;
    }

    const [, attempts, provider, errorJson] = match;

    try {
        const parsedError = JSON.parse(errorJson);
        if (parsedError.error && typeof parsedError.error === 'object') {
            const errorDetails = parsedError.error;

            // Extract learn more URL if present
            const learnMoreMatch = errorDetails.message?.match(/Learn more: (https?:\/\/[^\s]+)/);
            const learnMoreUrl = learnMoreMatch ? learnMoreMatch[1] : undefined;

            // Clean the message by removing the learn more URL
            const cleanMessage =
                errorDetails.message?.replace(/\s*Learn more: https?:\/\/[^\s]+/, '') || errorDetails.message;

            return {
                provider,
                attempts: parseInt(attempts, 10),
                errorDetails: {
                    code: errorDetails.code,
                    message: cleanMessage,
                    status: errorDetails.status,
                },
                learnMoreUrl,
            };
        }
    } catch {
        // Failed to parse JSON, not an AI provider error
    }

    return null;
}

function formatAiProviderError(aiError: AiProviderError): React.ReactNode {
    const getProviderDisplayName = (provider: string): string => {
        switch (provider.toLowerCase()) {
            case 'gemini':
                return 'Google Gemini';
            case 'openai':
                return 'OpenAI';
            case 'claude':
                return 'Claude';
            default:
                return provider;
        }
    };

    const getStatusMessage = (status: string): string => {
        switch (status) {
            case 'INVALID_ARGUMENT':
                return 'Invalid Request';
            case 'PERMISSION_DENIED':
                return 'Permission Denied';
            case 'QUOTA_EXCEEDED':
                return 'Quota Exceeded';
            case 'RATE_LIMITED':
                return 'Rate Limited';
            default:
                return status
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/\b\w/g, (l) => l.toUpperCase());
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-3 border-separator border-b pb-3">
                <Bot className="mt-0.5 flex-shrink-0 text-blue-500 dark:text-blue-400" size={18} />
                <div className="flex-1">
                    <p className="font-medium text-foreground">AI Service Unavailable</p>
                    <p className="mt-0.5 text-muted-foreground text-sm">
                        The {getProviderDisplayName(aiError.provider)} AI service encountered an error after{' '}
                        {aiError.attempts} attempt{aiError.attempts > 1 ? 's' : ''}.
                    </p>
                </div>
            </div>

            <div className="rounded-lg bg-muted p-3">
                <div className="mb-2 flex items-center gap-2">
                    <div className="rounded-full bg-red-100 px-2 py-1 dark:bg-red-900/30">
                        <span className="font-medium text-red-700 text-xs dark:text-red-400">
                            {getStatusMessage(aiError.errorDetails.status)}
                        </span>
                    </div>
                    <span className="text-subtle-foreground text-xs">Error {aiError.errorDetails.code}</span>
                </div>

                <p className="text-muted-foreground text-sm">{aiError.errorDetails.message}</p>

                {aiError.learnMoreUrl && (
                    <div className="mt-3">
                        <a
                            href={aiError.learnMoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            Learn more about this error
                            <ExternalLink size={12} />
                        </a>
                    </div>
                )}
            </div>

            <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/30">
                <p className="text-blue-800 text-sm dark:text-blue-200">
                    <strong>What to do:</strong> This is typically a temporary issue. Please try again in a few moments.
                    If the problem persists, you may need to contact support.
                </p>
            </div>
        </div>
    );
}

function formatZodIssues(issues: ZodIssue[]): React.ReactNode {
    if (issues.length === 0) {
        return (
            <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 flex-shrink-0 text-warning" size={18} />
                <div>
                    <p className="font-medium text-foreground">Validation Error</p>
                    <p className="mt-0.5 text-muted-foreground text-sm">Please check your input and try again.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-3 border-separator border-b pb-2">
                <AlertCircle className="mt-0.5 flex-shrink-0 text-red-500 dark:text-red-400" size={18} />
                <div>
                    <p className="mb-1 font-medium text-foreground">Validation Issues Found</p>
                    <p className="text-muted-foreground text-sm">Please review and correct the following:</p>
                </div>
            </div>
            <div className="space-y-2">
                {issues.map((issue, index) => {
                    const fieldName = createFieldNameFromPath(issue.path);
                    return (
                        <div key={index} className="flex items-start gap-3 py-2">
                            <div className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-red-400 dark:bg-red-500" />
                            <div className="min-w-0 flex-1">
                                <p className="font-medium text-foreground text-sm">{fieldName}</p>
                                <p className="mt-0.5 text-muted-foreground text-sm">{issue.message}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function formatFieldErrors(fieldErrors: Record<string, string[]>): React.ReactNode {
    debug('[ErrorFormatters] Field errors:', fieldErrors);

    if (Object.keys(fieldErrors).length === 0) {
        return (
            <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 flex-shrink-0 text-warning" size={18} />
                <div>
                    <p className="font-medium text-foreground">Validation Error</p>
                    <p className="mt-0.5 text-muted-foreground text-sm">Please check your input and try again.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-3 border-separator border-b pb-2">
                <AlertCircle className="mt-0.5 flex-shrink-0 text-red-500 dark:text-red-400" size={18} />
                <div>
                    <p className="mb-1 font-medium text-foreground">Validation Issues Found</p>
                    <p className="text-muted-foreground text-sm">Please review and correct the following:</p>
                </div>
            </div>
            <div className="space-y-2">
                {Object.entries(fieldErrors).map(([field, messages]) => {
                    // Check if the field might be a path string that needs parsing
                    let fieldName = field;
                    if (field.includes('.') || field.includes('[')) {
                        // Try to parse path-like field names
                        const pathParts = field.split(/[.[\]]+/).filter(Boolean);
                        fieldName = createFieldNameFromPath(pathParts);
                    } else {
                        fieldName = humanizeFieldName(field);
                    }

                    return (
                        <div key={field} className="flex items-start gap-3 py-2">
                            <div className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-red-400 dark:bg-red-500" />
                            <div className="min-w-0 flex-1">
                                <p className="font-medium text-foreground text-sm">{fieldName}</p>
                                <p className="mt-0.5 text-muted-foreground text-sm">{messages.join(', ')}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function formatNetworkError(errorDetails: NetworkErrorDetails): React.ReactNode {
    const getIconForErrorType = () => {
        switch (errorDetails.type) {
            case 'offline':
                return <WifiOff className="text-red-500 dark:text-red-400" size={20} />;
            case 'timeout':
                return <Clock className="text-warning" size={20} />;
            case 'server_error':
                return <Server className="text-red-500 dark:text-red-400" size={20} />;
            case 'dns':
            case 'network_failed':
                return <Globe className="text-red-500 dark:text-red-400" size={20} />;
            default:
                return <AlertCircle className="text-red-500 dark:text-red-400" size={20} />;
        }
    };

    return (
        <div className="space-y-4">
            {/* Header with icon and message */}
            <div className="flex items-start gap-3 border-separator border-b pb-3">
                {getIconForErrorType()}
                <div className="flex-1">
                    <p className="mb-1 font-medium text-foreground">{errorDetails.title}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{errorDetails.userMessage}</p>
                </div>
            </div>

            {/* Suggestions */}
            {errorDetails.suggestions.length > 0 && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/30">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" size={16} />
                        <div className="flex-1">
                            <p className="mb-2 font-medium text-blue-900 text-sm dark:text-blue-100">
                                What you can do:
                            </p>
                            <ul className="space-y-1 text-blue-800 text-sm dark:text-blue-200">
                                {errorDetails.suggestions.map((suggestion, index) => (
                                    <li key={index} className="flex items-start gap-2">
                                        <span className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400">•</span>
                                        <span>{suggestion}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Technical details */}
            {errorDetails.technicalMessage && (
                <div className="rounded-lg border border-separator bg-muted p-3">
                    <p className="mb-1 font-medium text-subtle-foreground text-xs">Technical Details</p>
                    <p className="break-all font-mono text-muted-foreground text-xs">{errorDetails.technicalMessage}</p>
                </div>
            )}
        </div>
    );
}
